// src/components/branch/OrderCard.tsx
import React, { useState, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, Check, Package, Truck, AlertCircle, 
  ChevronDown, ChevronUp, XCircle, ArrowLeft, 
  Eye, CalendarDays, ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Order, OrderStatus } from '../../types/types';

const STATUS_CONFIG: Record<OrderStatus, {
  color: string;
  icon: React.FC<any>;
  labelAr: string;
  labelEn: string;
  progress: number;
}> = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, labelAr: 'قيد الانتظار', labelEn: 'Pending', progress: 0 },
  approved: { color: 'bg-teal-100 text-teal-800', icon: Check, labelAr: 'تمت الموافقة', labelEn: 'Approved', progress: 25 },
  in_production: { color: 'bg-purple-100 text-purple-800', icon: Package, labelAr: 'في الإنتاج', labelEn: 'In Production', progress: 50 },
  completed: { color: 'bg-green-100 text-green-800', icon: Check, labelAr: 'مكتمل', labelEn: 'Completed', progress: 75 },
  in_transit: { color: 'bg-blue-100 text-blue-800', icon: Truck, labelAr: 'في النقل', labelEn: 'In Transit', progress: 90 },
  delivered: { color: 'bg-emerald-100 text-emerald-800', icon: Check, labelAr: 'تم التسليم', labelEn: 'Delivered', progress: 100 },
  cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle, labelAr: 'ملغى', labelEn: 'Cancelled', progress: 0 },
};

interface OrderCardProps {
  order: Order;
  isRtl: boolean;
  user: any;
  submitting: string | null;
  openConfirmDeliveryModal: (order: Order) => void;
}

const OrderCard: React.FC<OrderCardProps> = memo(({
  order,
  isRtl,
  user,
  submitting,
  openConfirmDeliveryModal,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  const displayedItems = expanded ? order.items : order.items.slice(0, 3);
  const hasMore = order.items.length > 3;

  // حساب الكمية الإجمالية المطلوبة
  const totalQuantity = order.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const unitLabel = isRtl ? 'وحدة' : 'unit';
  const totalQuantityDisplay = `${totalQuantity} ${unitLabel}`;

  const toggleExpand = useCallback(() => setExpanded(prev => !prev), []);

  const handleView = () => {
    navigate(`/branch-orders/${order.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-4"
      role="region"
      aria-labelledby={`order-${order.id}`}
    >
      <div className="p-3 bg-white shadow-md rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-300">
        <div className="flex flex-col gap-3">

          {/* رأس البطاقة: رقم الطلب + التاريخ + الكمية الإجمالية */}
          <div className={`flex items-start justify-between gap-3 ${isRtl ? 'flex-row' : ''}`}>
            <div className="flex-1 min-w-0">
              <h3 id={`order-${order.id}`} className="text-base font-semibold text-[15px] text-gray-800 truncate">
                {isRtl ? `طلب ${order.orderNumber}` : `Order #${order.orderNumber}`}
              </h3>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-600">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                  {order.date || (isRtl ? 'غير محدد' : 'Not set')}
                </span>
                <span className="flex items-center gap-1 font-medium text-indigo-700 whitespace-nowrap">
                  <Package className="w-3.5 h-3.5" />
                  {isRtl ? `الكمية: ${totalQuantityDisplay}` : `Qty: ${totalQuantityDisplay}`}
                </span>
              </div>
            </div>

            {/* حالة الطلب */}
            <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-medium flex items-center gap-1 ${status.color} ${isRtl ? 'flex-row-reverse' : ''}`}>
              <StatusIcon className="w-3 h-3" />
              {isRtl ? status.labelAr : status.labelEn}
            </span>
          </div>

          {/* شريط التقدم */}
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-amber-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${status.progress}%` }}
            />
          </div>

          {/* قائمة المنتجات */}
          <div className="p-2 bg-gray-50 rounded-md">
            <h4 className="text-xs font-semibold text-gray-900 mb-2">
              {isRtl ? 'المنتجات' : 'Products'}
            </h4>

            <ul className="space-y-2">
              {displayedItems.map((item) => {
                const requested = item.quantity;
                const received = item.receivedQuantity || 0;
                const unit = item.displayUnit || item.unit || (isRtl ? 'وحدة' : 'unit');
                const showArrow = received > 0 && !item.rejected;
                const isOver = received > requested;
                const isUnder = received < requested;

                return (
                  <li
                    key={item.itemId || item._id}
                    className={`p-2 rounded-md text-xs ${item.rejected ? 'bg-red-50 border border-red-200' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate flex-1">
                        {item.displayProductName || item.productName}
                        {item.rejected && (
                          <span className="text-red-600 text-[10px] mr-1">
                            ({isRtl ? 'مرفوض' : 'Rejected'})
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="mt-1 flex items-center justify-end gap-1 text-gray-700">
                      {item.rejected ? (
                        <span className="text-red-700 flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          {requested} {unit}
                        </span>
                      ) : (
                        <>
                          <span>{requested}</span>
                          <span className="text-gray-500">{unit}</span>
                          {showArrow && (isRtl ? <ArrowLeft className="w-3 h-3 text-gray-500" /> : <ArrowRight className="w-3 h-3 text-gray-500" />)}
                          {showArrow && (
                            <span className={isOver ? 'text-green-600' : isUnder ? 'text-amber-600' : 'text-gray-700'}>
                              {received} {unit}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* عرض المزيد */}
            {hasMore && (
              <button
                onClick={toggleExpand}
                className="w-full text-center text-amber-600 hover:text-amber-700 text-xs font-medium mt-3 flex items-center justify-center gap-1 transition-colors"
              >
                {expanded ? (
                  <>
                    {isRtl ? 'عرض أقل' : 'Show Less'} <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    {isRtl ? `+${order.items.length - 3} منتج آخر` : `+${order.items.length - 3} more items`} <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>

          {/* ملاحظات */}
          {order.notes && (
            <div className="p-1.5 bg-amber-50 rounded-md border border-amber-100">
              <p className="text-xs text-amber-800">
                <strong>{isRtl ? 'ملاحظات:' : 'Notes:'}</strong> {order.notes}
              </p>
            </div>
          )}

          {/* الأزرار */}
          <div className={`flex gap-1.5 ${isRtl ? 'justify-end' : 'justify-start'}`}>
            {/* زر العرض */}
            <button
              onClick={handleView}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-2.5 py-1 text-xs flex items-center gap-1 transition-all"
              aria-label={isRtl ? 'عرض الطلب' : 'View Order'}
            >
              <Eye className="w-3.5 h-3.5" />
              {isRtl ? 'عرض' : 'View'}
            </button>

            {/* زر تأكيد الاستلام */}
            {order.status === 'in_transit' && user?.role === 'branch' && (
              <button
                onClick={() => openConfirmDeliveryModal(order)}
                disabled={submitting === order.id}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg px-2.5 py-1 text-xs flex items-center gap-1 transition-all disabled:opacity-70"
              >
                <Truck className="w-3.5 h-3.5" />
                {submitting === order.id ? (isRtl ? 'جاري...' : 'Confirming...') : (isRtl ? 'استلام' : 'Receive')}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
});

OrderCard.displayName = 'OrderCard';
export default OrderCard;